import {Photo, Reminder} from '../types';

/**
 * Props passed to every template component
 */
export interface TemplateProps {
  // Photo data
  currentPhoto: Photo | null;
  currentPhotoUrl: string;
  previousPhoto: Photo | null;
  previousPhotoUrl: string | null;
  isTransitioning: boolean;
  scaledImageSize: {width: number; height: number};
  currentOffset: {x: number; y: number};
  previousOffset: {x: number; y: number};

  // Screen dimensions
  screenWidth: number;
  screenHeight: number;
  isPortrait: boolean;

  // Time & date
  currentTime: Date;
  formattedTime: string;
  formattedDate: {short: string; weekday: string};
  greeting: string;

  // User data
  userName: string;
  reminders: Reminder[];

  // UI state
  isEditMode: boolean;
  showSettings: boolean;
  showHint: boolean;
  isSaving: boolean;
  showSaved: boolean;
  loadingProgress: {loaded: number; total: number} | null;

  // Edit mode - pan offset for repositioning
  panOffset: {x: number; y: number};
  maxPan: {x: number; y: number};

  // Callbacks
  onSettingsPress: () => void;
  onEditPress: () => void;
  onExitEditMode: () => void;
  onDismissHint: () => void;

  // Edit mode drag handlers (web)
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  onDragMove: (e: React.MouseEvent | React.TouchEvent) => void;
  onDragEnd: () => void;

  // Edit mode pan responder (native)
  panResponderHandlers: any;

  // Translation function
  t: (key: string, options?: Record<string, unknown>) => string;

  // Display settings
  fontScale: number;
  displayDeviceName?: string;
}

/**
 * Photo section ratio for templates that don't use the full screen for photos
 */
export interface PhotoSectionRatio {
  landscape: {width: number; height: number};  // Ratios 0-1
  portrait: {width: number; height: number};   // Ratios 0-1
}

/**
 * Template metadata for registration
 */
export interface TemplateConfig {
  id: string;
  name: string;           // i18n key for display name
  description: string;    // i18n key for description
  thumbnail?: string;     // Optional preview image URL
  photoSectionRatio?: PhotoSectionRatio;  // Portion of screen used for photo (defaults to full screen)
}

/**
 * Full template definition including component
 */
export interface Template extends TemplateConfig {
  component: React.ComponentType<TemplateProps>;
}

/**
 * Time-based color scheme for dynamic theming
 */
export interface TimeBasedColors {
  primary: string;        // Main accent color
  text: string;           // Primary text color
  textMuted: string;      // Secondary text color
  background: string;     // Panel backgrounds
  gradient: string[];     // Gradient colors for overlays
  greeting: string;       // Greeting text color
  surface: string;        // Card/panel surface color
  border: string;         // Border color
}

/**
 * Time period enum for color scheme selection
 */
export type TimePeriod = 'morning' | 'afternoon' | 'evening' | 'night';

/**
 * Helper function to get the current time period
 */
export function getTimePeriod(date: Date): TimePeriod {
  const hour = date.getHours();

  if (hour >= 6 && hour < 12) {
    return 'morning';
  } else if (hour >= 12 && hour < 18) {
    return 'afternoon';
  } else if (hour >= 18 && hour < 21) {
    return 'evening';
  } else {
    return 'night';
  }
}
